import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconTrash } from "@tabler/icons-react";
import { STAGE_TYPE_LABELS } from "../lib/format";
import type { StageInput, StageType } from "../types/api";

const STAGE_TYPE_OPTIONS = Object.entries(STAGE_TYPE_LABELS).map(([value, label]) => ({ value, label }));

interface HiringFlowBuilderProps {
  stages: StageInput[];
  onChange: (stages: StageInput[]) => void;
}

export function HiringFlowBuilder({ stages, onChange }: HiringFlowBuilderProps) {
  function updateStage(index: number, patch: Partial<StageInput>) {
    onChange(stages.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }

  function moveStage(index: number, direction: -1 | 1) {
    const next = index + direction;
    if (next < 0 || next >= stages.length) return;
    const copy = [...stages];
    [copy[index], copy[next]] = [copy[next], copy[index]];
    onChange(copy);
  }

  function removeStage(index: number) {
    onChange(stages.filter((_, i) => i !== index));
  }

  function addStage() {
    onChange([...stages, { name: "New Stage", stage_type: "custom", is_mandatory: true }]);
  }

  return (
    <Stack gap="md">
      <Text size="sm" fw="var(--axio-font-weight-semibold)">
        Customize stages
      </Text>

      {stages.length === 0 ? (
        <Text c="dimmed" size="sm">
          No stages yet. Pick a template above or add stages manually.
        </Text>
      ) : (
        stages.map((stage, index) => (
          <Paper key={index} withBorder p="md">
            <Group align="flex-start" wrap="nowrap">
              <Stack gap="var(--axio-unit-4)" align="center" mt="var(--axio-unit-4)">
                <Text size="xs" c="dimmed" fw="var(--axio-font-weight-bold)">
                  {index + 1}
                </Text>
                <ActionIcon variant="subtle" size="sm" onClick={() => moveStage(index, -1)} disabled={index === 0}>
                  <IconArrowUp size="var(--axio-unit-14)" />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => moveStage(index, 1)}
                  disabled={index === stages.length - 1}
                >
                  <IconArrowDown size="var(--axio-unit-14)" />
                </ActionIcon>
              </Stack>
              <Stack gap="sm" style={{ flex: "var(--axio-number-one)" }}>
                <TextInput
                  label="Stage name"
                  value={stage.name}
                  onChange={(e) => updateStage(index, { name: e.currentTarget.value })}
                />
                <Select
                  label="Type"
                  data={STAGE_TYPE_OPTIONS}
                  value={stage.stage_type}
                  onChange={(v) => v && updateStage(index, { stage_type: v as StageType })}
                />
                <Checkbox
                  label="Mandatory"
                  checked={stage.is_mandatory}
                  onChange={(e) => updateStage(index, { is_mandatory: e.currentTarget.checked })}
                />
              </Stack>
              <ActionIcon color="red" variant="subtle" mt="var(--axio-unit-24)" onClick={() => removeStage(index)}>
                <IconTrash size="var(--axio-unit-16)" />
              </ActionIcon>
            </Group>
          </Paper>
        ))
      )}

      <Button variant="light" onClick={addStage}>
        + Add stage
      </Button>
    </Stack>
  );
}
